import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { CategoriesService, MappingOfStatusesService, TaxReturnStatusesService, WkTranslatePipe } from "@app/shared";
import { ITaxReturnStatus } from "src/app/views-interfaces/tax-return-status-model.interface";
import { DashboardsService, HelpersService } from "../../shared";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";
import { Member } from "../member-interface";

@Component({
    selector: "follow-up-info",
    templateUrl: "./follow-up-info.component.html",
    styleUrl: "./follow-up-info.component.scss",
    standalone: false,
    host: { class: "wk-fup" },
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [WkTranslatePipe],
})
export class FollowUpInfoComponent implements OnInit, OnChanges {
    private readonly wkTranslate = inject(WkTranslatePipe);
    private readonly translate = inject(TranslateService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly helpersService = inject(HelpersService);
    private readonly taxReturnStatusesService = inject(TaxReturnStatusesService);

    @Input() member: Member;
    readonly statusChanged = output<any>();
    readonly categoryChanged = output<any>();
    readonly categoriesViewModelChanged = output<any>();

    categories: any;
    categoriesViewModel: any = {};
    mappedStatuses: any[] = [];
    statuses: any = [];
    steps: any;
    loaded = false;
    currentDashboard: IDashboardModel;
    currentLanguage = "en-CA";

    constructor() {
        this.translate.onLangChange.subscribe((event) => {
            this.currentLanguage = event.lang;
        });
    }

    async ngOnInit() {
        this.currentLanguage = this.helpersService.getLang();
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.categories = await this.categoriesService.getCategoriesList();
        this.categories = [
            {
                Name: {
                    // TODO : find a way to translate this and preserve search input in dropdown list
                    "en-CA": this.translate.instant("General.UnassignedFem"),
                    "fr-CA": this.translate.instant("General.UnassignedFem"),
                },
                Id: null,
            },
            ...this.categories,
        ];
        if (!this.helpersService.stepsMerged) {
            await this.helpersService.mergeSteps();
        }
        this.statuses = await this.taxReturnStatusesService.getTaxReturnStatusesList();
        this.getMappedStatuses();
        this.steps = this.mappingOfStatusesService.Steps;
        this.generateCategoriesViewModel();
    }

    ngOnChanges() {
        if (this.currentDashboard) {
            this.generateCategoriesViewModel();
            if (this.loaded) this.getMappedStatuses();
            this.loaded = true;
        }
    }

    getMappedStatuses() {
        this.mappedStatuses = this.mappingOfStatusesService
            .getAllReturnStatuses()
            .filter((status: { TaxReturnStatus: ITaxReturnStatus }) => status.TaxReturnStatus?.Active);
        if (!this.getStatus()) {
            this.mappedStatuses.unshift({
                TaxReturnStatus: {
                    Name: {
                        // TODO : find a way to translate this and preserve search input in dropdown list
                        "en-CA": this.wkTranslate.transform("General.None"),
                        "fr-CA": this.wkTranslate.transform("General.None"),
                    },
                    Id: this.member.statusId,
                },
            });
        } else if (this.getStatus() && (!this.isMapped() || !this.getStatus().Active)) {
            this.mappedStatuses.unshift({
                TaxReturnStatus: {
                    Name: {
                        // TODO : find a way to translate this and preserve search input in dropdown list
                        "en-CA": `${this.wkTranslate.transform("General.Inactive")}${this.wkTranslate.transform(this.getStatus().Name)}`,
                        "fr-CA": `${this.wkTranslate.transform("General.Inactive")}${this.wkTranslate.transform(this.getStatus().Name)}`,
                    },
                    Id: this.member.statusId,
                },
                disabled: true,
            });
        }
    }

    getStepNameFromStatusId(statusId: string): any {
        const step = this.mappingOfStatusesService.getStepFromStatusId(statusId);
        if (step) {
            return step.Name;
        } else {
            return null;
        }
    }

    generateCategoriesViewModel() {
        const calculatedCategory = this.categoriesService.calculateCategory(this.currentDashboard, this.member.tags);
        const calculatedCategoryId = calculatedCategory?.Id ?? null;

        // Determine if category was overridden by the user.
        // Legend for member.categoryId:
        //  - null: The user has overridden the category with "Unassigned"
        //  - Unassigned: The user has not made any overrides on the category
        //  - A GUID: The user has overriden the category with a category of his choice
        let selectedCategory: string | null;
        if (this.member.categoryId === null) {
            // Explicit "Unassigned" override -> show Unassigned instead of the calculated fallback.
            selectedCategory = null;
        } else if (this.member.categoryId === "Unassigned" || !this.categoriesService.getCategory(this.member.categoryId)) {
            // No override (or stale/invalid id) -> fall back to the system-calculated category.
            selectedCategory = calculatedCategoryId;
        } else {
            selectedCategory = this.member.categoryId;
        }

        const isCategoryOverridden = this.isOverrideSelection(selectedCategory, calculatedCategoryId);

        this.categoriesViewModel[this.member.id] = {
            isCategoryOverridden: isCategoryOverridden,
            selectedCategory: selectedCategory,
            calculatedCategory: calculatedCategory,
        };
        this.categoriesViewModelChanged.emit(this.categoriesViewModel);
    }

    changeStatus(statusId: string) {
        this.member.statusId = statusId;
        this.statusChanged.emit({ memberId: this.member.id, statusId: statusId });
    }

    changeCategory(categoryId: string) {
        this.member.categoryId = categoryId;
        const viewModel = this.categoriesViewModel[this.member.id];
        if (viewModel) {
            // Unassigned may come through as null (Id: null on the dropdown option) or "Unassigned";
            // treat both as "no selection" for the override comparison.
            const normalizedCategoryId = categoryId === null || categoryId === "Unassigned" ? null : categoryId;
            const calculatedCategoryId = viewModel.calculatedCategory?.Id ?? null;
            const isCategoryOverridden = this.isOverrideSelection(normalizedCategoryId, calculatedCategoryId);

            viewModel.selectedCategory = normalizedCategoryId;
            viewModel.isCategoryOverridden = isCategoryOverridden;
        }
        this.categoryChanged.emit({ memberId: this.member.id, categoryId: categoryId });
    }

    private isOverrideSelection(selectedCategoryId: string | null, calculatedCategoryId: string | null): boolean {
        return selectedCategoryId !== null && selectedCategoryId !== calculatedCategoryId;
    }

    isMapped() {
        return this.mappedStatuses.some((mapped) => mapped.TaxReturnStatus?.Id === this.member.statusId);
    }

    getStatus() {
        return this.statuses.find((it) => it.Id === this.member.statusId);
    }
}
